import React, { useEffect, useRef, memo } from 'react';
import { LineStyle, LineType, LineSeries, createSeriesMarkers } from 'lightweight-charts';
import { sanitizeChartData } from './utils';

/**
 * IndicatorOverlay Component
 * Renders an indicator ON TOP of the main chart (e.g., SMA, EMA, Bollinger Bands).
 * It does NOT render its own DOM element; it uses the parent chart instance.
 * 
 * @param {Object} props
 * @param {Object} props.chartInstance - Reference to main LightweightCharts instance
 * @param {string} props.id - Unique ID
 * @param {string} props.type - 'SMA', 'EMA', etc.
 * @param {Array} props.data - Data series { time, value }
 * @param {string} props.color - Line color
 * @param {number} props.lineWidth - Line width
 * @param {number} props.lineStyle - Line style enum
 * @param {number} props.lineType - Line type enum
 */
const IndicatorOverlay = ({ 
    chartInstance, 
    id, 
    type,
    data, 
    color = '#FF9800', 
    lineWidth = 2,
    lineStyle = LineStyle.Solid,
    lineType = LineType.Simple,
    onSeriesCreated
}) => {
    const seriesRef = useRef(null);

    // 1. Manage Series Lifecycle (Creation/Removal)
    useEffect(() => {
        if (!chartInstance) return;

        // Create Series
        // We assume LineSeries for most overlays like SMA/EMA
        const series = chartInstance.addSeries(LineSeries, {
            // Initial options
            color: color,
            lineWidth: lineWidth,
            lineStyle: lineStyle,
            lineType: lineType,
            crosshairMarkerVisible: false, 
            priceLineVisible: false,
            lastValueVisible: true, 
            title: `${type}`, 
        });

        seriesRef.current = series;
        
        if (onSeriesCreated) {
            onSeriesCreated(id, series);
        }

        return () => {
            // Signal removal immediately so new mount can override, not get wiped by the timeout
            if (onSeriesCreated) {
                onSeriesCreated(id, null);
            }
            seriesRef.current = null;
            
            // Defer cleanup to allow parent unmount to destroy chart first
            // Prevents "Object is disposed" in lightweight-charts RAF
            setTimeout(() => {
                if (chartInstance && series) {
                    try {
                        chartInstance.removeSeries(series);
                    } catch (e) {
                        // Benign error: Chart might already be destroyed
                    }
                }
            }, 0);
        };
    }, [chartInstance, id, type]); // removed color/lineWidth/styles from here

    // 2. Handle Style Updates (Zero-Flicker)
    useEffect(() => {
        if (seriesRef.current) {
            seriesRef.current.applyOptions({
                color,
                lineWidth,
                lineStyle,
                lineType
            });
        }
    }, [color, lineWidth, lineStyle, lineType]);

    // 3. Manage Data & Markers
    useEffect(() => {
        if (!seriesRef.current || !data) return;
        
        try {
            // Extract standard data (scalar)
            seriesRef.current.setData(sanitizeChartData(data));

            // Extract Markers (if any)
            const markers = data
                .filter(d => d.value && typeof d.value === 'object' && d.value.marker)
                .map(d => {
                    const trend = (d.value.trend || 'sideways').toLowerCase();
                    return {
                        time: d.time,
                        position: trend === 'up' ? 'belowBar' : trend === 'down' ? 'aboveBar' : 'inBar',
                        color: trend === 'up' ? '#10B981' : trend === 'down' ? '#EF4444' : '#9CA3AF',
                        shape: trend === 'up' ? 'arrowUp' : trend === 'down' ? 'arrowDown' : 'circle',
                        text: d.value.marker,
                        size: 2
                    };
                });

            //* F-IO-1: UNREACHABLE for main-pane markers - SmartChart.jsx:1740 returns [] for marker / dee_trend_slope overlays and :1800-1803 hands this component scalar per-key values, so `d.value.marker` never exists here.
            if (markers.length > 0) {
                // V5 Plugin System: Use createSeriesMarkers
                if (!seriesRef.current._markersPlugin) {
                    seriesRef.current._markersPlugin = createSeriesMarkers(seriesRef.current, markers);
                } else {
                    seriesRef.current._markersPlugin.setMarkers(markers);
                }
            } else if (seriesRef.current && seriesRef.current._markersPlugin) {
                 seriesRef.current._markersPlugin.setMarkers([]);
            }
        } catch (err) {
            console.error(`[IndicatorOverlay:${type}] Data error:`, err);
        }
    }, [data]);

    // Return null because we don't render DOM nodes
    return null;
};

export default memo(IndicatorOverlay);
